import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Smartphone } from "lucide-react";
import { fetchExpert } from "@/lib/api";
import { describeExpert, sizeText, useTitle } from "@/lib/brand";
import { Facts, Loading } from "@/components/Kit";
import Mark from "@/components/Mark";

/** One Expert — the app's details sheet, as a page. Rebuilt 2026-10-27. */
export default function ExpertDetailPage() {
  const { slug } = useParams();
  const [expert, setExpert] = useState(null);
  const [error, setError] = useState(null);
  const d = expert ? describeExpert(expert) : null;
  useTitle(d?.title || "Expert");

  useEffect(() => {
    fetchExpert(slug).then(setExpert).catch(() => setError("We couldn't find that Expert."));
  }, [slug]);

  return (
    <main data-testid="expert-detail" className="ex-page">
      <div className="ex-wrap pb-28">
        <Link to="/experts" className="ex-btn ex-btn-quiet ex-btn-sm -ml-4 mt-8"><ArrowLeft className="h-4 w-4" strokeWidth={1.8} /> All Experts</Link>
        {error && <div className="ex-panel p-10 mt-8 text-center text-white">{error}</div>}
        {!error && !expert && <Loading label="Loading" />}
        {expert && (
          <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr] pt-10">
            <div>
              <div className="flex flex-wrap gap-2 ex-rise">
                {d.engine && <span className="ex-chip">{d.engine}</span>}
                <span className="ex-chip">Free</span>
                {d.edition && <span className="ex-chip">{d.edition}</span>}
              </div>
              <h1 className="ex-h1 mt-6 ex-rise-2">{d.title}</h1>
              {d.line && <div className="ex-small mt-3 ex-rise-2">{d.line}</div>}
              {expert.persona && <p className="ex-lede mt-6 max-w-2xl ex-rise-3">{expert.persona}</p>}

              <div className="mt-10 ex-rise-4">
                <Facts rows={[
                  ["Subject area", expert.domain],
                  ["Runs on", d.engine ? `The ${d.engine}` : expert.engine_id],
                  ["Trained", d.date],
                  ["Edition", d.edition],
                  ["Size", sizeText(expert)],
                  ["Price", "Free"],
                  expert.sha256 ? ["SHA-256", <span key="h" className="ex-mono break-all text-[color:var(--ex-text2)]">{expert.sha256}</span>] : null,
                ].filter((r) => r && r[1])} />
              </div>

              {(expert.sample_qa || []).length > 0 && (
                <div className="mt-12">
                  <div className="ex-eyebrow mb-4">Sample answers</div>
                  <div className="space-y-3">
                    {expert.sample_qa.map((qa, i) => (
                      <div key={i} className="ex-card p-6">
                        <div className="text-white font-medium">{qa.q}</div>
                        <div className="ex-body mt-2">{qa.a}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:pt-2">
              <div className="ex-panel p-7 lg:sticky lg:top-24">
                <div className="flex justify-center py-4"><Mark size={96} strokeBoost={2} className="text-white" /></div>
                <div className="space-y-3 mt-4">
                  <div className="flex items-start gap-3">
                    <Smartphone className="h-5 w-5 text-white mt-0.5 flex-none" strokeWidth={1.7} />
                    <div>
                      <div className="text-white font-medium">Install it from the app</div>
                      <div className="ex-small mt-1">Open Experts, tap <span className="text-white">Get an Expert</span> and choose it — one tap, no file to move.</div>
                    </div>
                  </div>
                </div>
                {expert.available && expert.download_url && (
                  <a href={expert.download_url} className="ex-btn ex-btn-secondary ex-btn-block mt-6" data-testid="expert-download-btn">
                    <Download className="h-4 w-4" strokeWidth={2} /> Download the file{sizeText(expert) ? ` · ${sizeText(expert)}` : ""}
                  </a>
                )}
                <p className="ex-tiny mt-4">An Expert runs only on the Engine it was made for{d.engine ? ` — this one needs the ${d.engine}` : ""}. Open a downloaded .expert file with the app.</p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
